import {
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  getCountFromServer,
  limit,
  type QueryConstraint,
  type Query,
} from "firebase/firestore";

/** Rango temporal común */
export type Range =
  | { kind: "lastDays"; days: number }
  | { kind: "between"; from: Date; to: Date };

/** KPIs de comunidad + extras opcionales para Home */
export type Kpis = {
  postsTotal: number;
  likesTotal: number;
  recommendsTotal: number;
  commentsTotal: number;
  // Extras para Home (opcionales)
  usersTotal?: number;
  certificationsTotal?: number;
};

/** Bucket diario para comunidad */
export type DailyBucket = {
  date: string; // YYYY-MM-DD
  posts: number;
  likes: number;
  recommends: number;
  comments: number;
};

/** KPIs de certificaciones */
export type CertsKpis = {
  total: number;
  official: number;
  trial: number;
  draft: number;
  final: number;
};

/* ───────────────────── helpers ───────────────────── */

/** Construye filtros por fecha sobre el campo indicado */
function rangeToWhere(
  range: Range | undefined,
  field: "createdAt" | "updatedAt"
): QueryConstraint[] {
  if (!range) return [];
  if (range.kind === "lastDays") {
    const to = Timestamp.now();
    const from = Timestamp.fromDate(
      new Date(Date.now() - range.days * 86_400_000)
    );
    return [where(field, ">=", from), where(field, "<=", to)];
  }
  return [
    where(field, ">=", Timestamp.fromDate(range.from)),
    where(field, "<=", Timestamp.fromDate(range.to)),
  ];
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* Helpers para certificaciones con diagnóstico */

type CountResult = {
  ok: boolean;
  total: number;
  official: number;
  trial: number;
  draft: number;
  final: number;
};

/**
 * Versión original: cuenta por type/status en un Query que SÍ tiene `type`.
 * Se mantiene intacta para no romper usos futuros.
 */
async function countOn(
  ref: Query,
  timeWhere: QueryConstraint[],
  label: string
): Promise<CountResult> {
  try {
    // Muestra de hasta 3 docs para ver estructura
    try {
      const sample = await getDocs(query(ref, limit(3)));
      console.info(
        `[stats] sample (${label})`,
        sample.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    } catch (e) {
      console.info(
        `[stats] sample (${label}) no disponible:`,
        (e as Error).message
      );
    }

    const [cOfficial, cTrial, cDraft, cFinal] = await Promise.all([
      getCountFromServer(
        query(ref, where("type", "==", "official"), ...timeWhere)
      ),
      getCountFromServer(
        query(ref, where("type", "==", "trial"), ...timeWhere)
      ),
      getCountFromServer(
        query(ref, where("status", "==", "draft"), ...timeWhere)
      ),
      getCountFromServer(
        query(ref, where("status", "==", "final"), ...timeWhere)
      ),
    ]);

    const official = cOfficial.data().count;
    const trial = cTrial.data().count;
    const draft = cDraft.data().count;
    const final = cFinal.data().count;
    const total = official + trial;

    console.info(`[stats] counts (${label}) =>`, {
      total,
      official,
      trial,
      draft,
      final,
    });

    return { ok: true, total, official, trial, draft, final };
  } catch (e) {
    console.warn(`[stats] countOn(${label}) error:`, (e as Error).message);
    return { ok: false, total: 0, official: 0, trial: 0, draft: 0, final: 0 };
  }
}

/**
 * NUEVO: contador para collection groups SIN campo `type`.
 * - `total` = número de docs del grupo (aplica rango si se pasa)
 * - `draft`/`final` por status
 * Útil para users//evaluations y users//trialEvaluations.
 */
async function countGroupBasic(
  ref: Query,
  timeWhere: QueryConstraint[],
  label: string
): Promise<{ total: number; draft: number; final: number }> {
  // helper: intenta contar con constraints primarios y, si falta índice,
  // reintenta con fallback (sin rango de fechas).
  const safeCount = async (
    primary: QueryConstraint[],
    fallback: QueryConstraint[],
    what: string
  ): Promise<number> => {
    try {
      const snap = await getCountFromServer(query(ref, ...primary));
      return snap.data().count;
    } catch (e: any) {
      if (e?.code === "failed-precondition") {
        console.warn(
          `[stats] missing index for ${label} (${what}) — retrying without date filter`
        );
        const snap = await getCountFromServer(query(ref, ...fallback));
        return snap.data().count;
      }
      throw e;
    }
  };

  // muestra de 3 docs para diagnóstico (igual que antes)
  try {
    const sample = await getDocs(query(ref, limit(3)));
    // console.info(
    //   `[stats] sample (${label})`,
    //   sample.docs.map((d) => {
    //     const data = d.data() as any;
    //     const createdAt =
    //       data?.createdAt?.toDate?.() ?? data?.createdAt ?? null;
    //     const updatedAt =
    //       data?.updatedAt?.toDate?.() ?? data?.updatedAt ?? null;
    //     return {
    //       path: d.ref.path,
    //       status: data?.status ?? null,
    //       createdAt,
    //       updatedAt,
    //     };
    //   })
    // );
  } catch (e) {
    console.info(
      `[stats] sample (${label}) no disponible:`,
      (e as Error).message
    );
  }

  // total (con rango; si falta índice, sin rango)
  const total = await safeCount(
    [...timeWhere],
    [], // all-time fallback
    "total"
  );

  // draft + final (status + rango; fallback: solo status)
  const draft = await safeCount(
    [where("status", "==", "draft"), ...timeWhere],
    [where("status", "==", "draft")],
    "draft"
  );
  const final = await safeCount(
    [where("status", "==", "final"), ...timeWhere],
    [where("status", "==", "final")],
    "final"
  );

  // console.info(`[stats] counts (${label}) =>`, { total, draft, final });
  return { total, draft, final };
}

/* ───────────────────── Comunidad ───────────────────── */
/**
 * KPIs + serie diaria de comunidad.
 * Además inyecta usersTotal y certificationsTotal para Home.
 */
export async function getStats(
  range: Range = { kind: "lastDays", days: 30 }
): Promise<{ kpis: Kpis; daily: DailyBucket[] }> {
  const db = getFirestore();
  const whereRange = rangeToWhere(range, "createdAt");

  // ---------- Posts de comunidad ----------
  const postsRef = collection(db, "communityPosts");
  const postsSnap = await getDocs(
    query(postsRef, ...whereRange, orderBy("createdAt", "asc"))
  );

  let postsTotal = 0;
  let likesTotal = 0;
  let recommendsTotal = 0;
  let commentsTotal = 0;

  const buckets = new Map<string, DailyBucket>();

  postsSnap.forEach((doc) => {
    const d = doc.data() as any;
    const createdAt: Date =
      d.createdAt?.toDate?.() ?? d.createdAt ?? new Date();
    const key = ymd(createdAt);

    const like = Number(d?.counters?.like ?? 0) || 0;
    const rec = Number(d?.counters?.recommend ?? 0) || 0;
    const com = Number(d?.counters?.comment ?? 0) || 0;

    postsTotal += 1;
    likesTotal += like;
    recommendsTotal += rec;
    commentsTotal += com;

    const cur = buckets.get(key) ?? {
      date: key,
      posts: 0,
      likes: 0,
      recommends: 0,
      comments: 0,
    };
    cur.posts += 1;
    cur.likes += like;
    cur.recommends += rec;
    cur.comments += com;
    buckets.set(key, cur);
  });

  // ---------- Serie diaria (rellena huecos) ----------
  const outDaily: DailyBucket[] = [];
  let startDate: Date;
  let endDate: Date;

  if (range.kind === "lastDays") {
    endDate = new Date();
    startDate = new Date(Date.now() - range.days * 86_400_000);
  } else {
    startDate = range.from;
    endDate = range.to;
  }

  for (
    let t = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    t <= endDate;
    t = new Date(t.getTime() + 86_400_000)
  ) {
    const key = ymd(t);
    outDaily.push(
      buckets.get(key) ?? {
        date: key,
        posts: 0,
        likes: 0,
        recommends: 0,
        comments: 0,
      }
    );
  }

  // ---------- Extras para Home (usuarios + certificaciones) ----------
  // Totales all-time SIN filtros (evita "—" por errores de rango)
  const usersCol = collection(db, "users");
  const usersCountSnap = await getCountFromServer(usersCol);

  const certsKpis = await getCertsStats(); // sin rango => totales all-time

  const usersTotal = usersCountSnap.data().count;
  const certificationsTotal = certsKpis.total;

  return {
    kpis: {
      postsTotal,
      likesTotal,
      recommendsTotal,
      commentsTotal,
      usersTotal,
      certificationsTotal,
    },
    daily: outDaily,
  };
}

/* ───────────────────── Certificaciones ───────────────────── */
/**
 * KPIs de certificaciones por tipo/estado.
 * Si pasas `range` aplica el filtro por **updatedAt** (más estable en UI); si no, totales all-time.
 * Fuente real de datos: collection groups:
 *   - users//evaluations        → oficiales
 *   - users//trialEvaluations   → de prueba
 *
 * (Se mantienen helpers y logs para diagnóstico.)
 */
export async function getCertsStats(range?: Range): Promise<CertsKpis> {
  const db = getFirestore();

  // usamos updatedAt porque es el campo visible/estable en la lista
  const timeWhere = rangeToWhere(range, "updatedAt");

  // grupos reales
  const cgOfficial = collectionGroup(db, "evaluations");
  const cgTrial = collectionGroup(db, "trialEvaluations");

  // conteos básicos por grupo (totales y por status)
  const [off, tri] = await Promise.all([
    countGroupBasic(cgOfficial, timeWhere, "users/*/evaluations (official)"),
    countGroupBasic(cgTrial, timeWhere, "users/*/trialEvaluations (trial)"),
  ]);

  const official = off.total;
  const trial = tri.total;
  const draft = off.draft + tri.draft;
  const final = off.final + tri.final;

  const out: CertsKpis = {
    total: official + trial,
    official,
    trial,
    draft,
    final,
  };

  // console.info(
  //   "[stats] getCertsStats -> source: collectionGroup(evaluations + trialEvaluations), range:",
  //   range ?? "(all-time)",
  //   "=>",
  //   out
  // );
  return out;
}

// Alias que usa tu Home
export const getAppStats = getStats;
